import React, { useContext, useState } from "react";
import { Modal } from "react-bootstrap";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { CommonContext } from "hooks/CommonContext";
import { apiEndpoint } from "services/apiEndpoints";
import apiInterceptor from "services/apiInterceptor";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

  /* Dark modal */
  .fp-modal .modal-content {
    background: #0f1629 !important;
    border: 1px solid #1e2d4a !important;
    border-radius: 18px !important;
    box-shadow: 0 24px 64px rgba(0,0,0,0.6) !important;
    font-family: 'Inter', system-ui, sans-serif !important;
  }

  .fp-modal .modal-body { padding: 0 !important; }

  .fp-inner { padding: 28px 28px 24px; }

  .fp-title {
    font-size: 18px;
    font-weight: 700;
    color: #f0f4ff;
    margin: 0 0 18px;
  }

  /* Textarea container */
  .fp-box {
    width: 100%;
    height: 220px;
    background: #080d18;
    border: 1px solid #1e2d4a;
    border-radius: 12px;
    overflow: hidden;
    transition: border-color 0.15s;
  }

  .fp-box:focus-within { border-color: #22d3ee; }

  .fp-textarea {
    width: 100%;
    height: 100%;
    background: transparent;
    border: none;
    outline: none;
    resize: none;
    padding: 14px 16px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 14px;
    color: #dbe4f8;
    line-height: 1.7;
    caret-color: #22d3ee;
    box-sizing: border-box;
  }

  .fp-textarea::placeholder { color: #4a5f82; }

  .fp-textarea:disabled { opacity: 0.5; cursor: not-allowed; }

  .fp-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 18px;
  }

  /* Cancel */
  .fp-btn-cancel {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    padding: 10px 20px;
    background: #162035;
    color: #8b9dc3;
    border: 1px solid #1e2d4a;
    border-radius: 10px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
  }

  .fp-btn-cancel:hover { background: #1e2d4a; color: #c4d0ea; }

  /* Submit — cyan */
  .fp-btn-submit {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 22px;
    background: linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%);
    color: #04111a;
    border-radius: 10px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13.5px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 3px 14px rgba(34,211,238,0.25);
    transition: filter 0.15s, transform 0.12s;
    white-space: nowrap;
  }

  .fp-btn-submit:hover:not(:disabled) {
    filter: brightness(1.08);
    transform: translateY(-1px);
  }

  .fp-btn-submit:disabled {
    background: #1e2d4a;
    color: #526080;
    box-shadow: none;
    cursor: not-allowed;
    filter: none;
    transform: none;
  }

  /* Spinner */
  .fp-spinner {
    width: 13px;
    height: 13px;
    border: 2px solid rgba(4,17,26,0.3);
    border-top-color: #04111a;
    border-radius: 50%;
    animation: fpSpin 0.7s linear infinite;
    flex-shrink: 0;
  }

  @keyframes fpSpin { to { transform: rotate(360deg); } }
`;

let _inj = false;
const inject = () => {
  if (_inj) return;
  const s = document.createElement("style");
  s.textContent = STYLES;
  document.head.appendChild(s);
  _inj = true;
};

const FeedbackPopup = ({ show, handleClose, setTitleDescription, setIsFeedbackSubmitted }) => {
  inject();

  const { interview_id } = useParams();
  const queryParams = new URLSearchParams(window.location.search);
  const cid = queryParams.get("cid");
  const { note, setNote, feedbackGuid, setFeedbackGuid } = useContext(CommonContext);

  const [feedbackNotes, setFeedbackNotes] = useState(note ?? "");
  const [isLoading, setIsLoading] = useState(false);

  const feedbackSubmitHandler = async (guid) => {
    setIsLoading(true);
    const roundFeedbackGuid = guid || feedbackGuid;
    const data = { feedback: feedbackNotes, interviewGuid: interview_id, interviewerGuid: cid };
    try {
      await apiInterceptor
        .put(`${apiEndpoint.interviewFeedback}/${roundFeedbackGuid}`, data)
        .then(() => { handleClose(); setNote(feedbackNotes); })
        .catch((err) => console.error("Error submitting feedback:", err));
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const feedbackPostPutHandler = () => {
    setIsLoading(true);
    setTitleDescription({
      title: "Your Feedback Submitted",
      description: "Thank you for your feedback!",
    });
    setIsFeedbackSubmitted(true);
    const data = { feedback: feedbackNotes, interviewGuid: interview_id, interviewerGuid: cid };

    if (feedbackGuid.length > 0) {
      feedbackSubmitHandler();
    } else {
      const formData = new FormData();
      formData.append(
        "interviewFeedbackFilter",
        new Blob([JSON.stringify(data)], { type: "application/json" })
      );
      apiInterceptor
        .post(apiEndpoint.interviewFeedback, formData, { headers: { "Content-Type": "multipart/form-data" } })
        .then((response) => {
          if (response.data.status === "ERROR") {
            setFeedbackGuid(response.data.statusGUID);
            feedbackSubmitHandler(response.data.statusGUID);
          } else {
            setFeedbackGuid(response.data.data);
          }
          handleClose();
        })
        .catch((err) => console.error(err))
        .finally(() => setIsLoading(false));
    }
  };

  return (
    <Modal centered show={show} onHide={handleClose} size="lg" dialogClassName="fp-modal">
      <Modal.Body>
        <div className="fp-inner">
          <h2 className="fp-title">Give Feedback</h2>

          <div className="fp-box">
            <textarea
              className="fp-textarea"
              placeholder="Share your thoughts on this interview..."
              value={feedbackNotes}
              onChange={(e) => setFeedbackNotes(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="fp-actions">
            <button className="fp-btn-cancel" onClick={handleClose} disabled={isLoading}>
              Cancel
            </button>
            <button
              className="fp-btn-submit"
              onClick={feedbackPostPutHandler}
              disabled={isLoading || feedbackNotes.trim().length === 0}
            >
              {isLoading && <span className="fp-spinner" />}
              {isLoading ? "Submitting…" : "Submit Feedback"}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

FeedbackPopup.propTypes = {
  show: PropTypes.any.isRequired,
  handleClose: PropTypes.any.isRequired,
  setTitleDescription: PropTypes.func.isRequired,
  setIsFeedbackSubmitted: PropTypes.func.isRequired,
};

export default FeedbackPopup;